import { parseISO } from "date-fns"
import { BillingAddress, RetroCommissionSnapshot } from "../queries"

export type MetricCategoryKey = 'client-volume' | 'flow-data' | 'commission'

const toIntlLocale = (language: string) => language.startsWith('fr') ? 'fr-FR' : 'en-US'

export type MetricRow = {
    key: string
    categoryKey: MetricCategoryKey
    category: string
    label: string
    valueType: 'number' | 'currency'
    getValue?: (snapshot: RetroCommissionSnapshot) => number
    noSection?: boolean
}

export const sortSnapshotsByPeriod = (snapshots: RetroCommissionSnapshot[]) =>
    [...snapshots].sort((first, second) => first.periodStart.localeCompare(second.periodStart))

export const formatAmount = (amountInCents: number, language: string) =>
    new Intl.NumberFormat(toIntlLocale(language), {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
    }).format((amountInCents || 0) / 100)

export const formatPeriodLong = (value: Date, language: string) =>
    new Intl.DateTimeFormat(toIntlLocale(language), { month: 'short', year: 'numeric' }).format(value)

export const formatPeriod = (periodStart: string, language: string) => {
    const periodDate = parseISO(periodStart)
    const month = new Intl.DateTimeFormat(toIntlLocale(language), { month: 'short' })
        .format(periodDate).replace('.', '').toUpperCase()
    return `${month} ${periodDate.getFullYear()}`
}

export const buildMetricRows = (billingAddress: BillingAddress, t: (key: string) => string): MetricRow[] => {
    const { retroCommissionSnapshots } = billingAddress
    const showBookingRoom = retroCommissionSnapshots.some((s) => (s.bookingRoomCommissionAmount ?? 0) !== 0)
    const showBusinessReferral = retroCommissionSnapshots.some((s) => (s.businessReferralAmount ?? 0) !== 0)

    const rows: MetricRow[] = [
        {
            key: 'activeCount',
            categoryKey: 'client-volume',
            category: t('facturation.table-categories.client-volume'),
            label: t('facturation.table.active'),
            valueType: 'number',
            getValue: (s) => s.activeCount
        },
        {
            key: 'unpaidCount',
            categoryKey: 'client-volume',
            category: t('facturation.table-categories.client-volume'),
            label: t('facturation.table.unpaid'),
            valueType: 'number',
            getValue: (s) => s.unpaidCount
        },
        {
            key: 'totalCount',
            categoryKey: 'client-volume',
            category: t('facturation.table-categories.client-volume'),
            label: t('facturation.table.total'),
            valueType: 'number',
            getValue: (s) => s.totalCount
        },
        {
            key: 'canceledCount',
            categoryKey: 'client-volume',
            category: t('facturation.table-categories.client-volume'),
            label: t('facturation.table.canceled'),
            valueType: 'number',
            getValue: (s) => s.canceledCount
        },
        {
            key: 'newCustomersCount',
            categoryKey: 'flow-data',
            category: t('facturation.table-categories.flow-data'),
            label: t('facturation.table.new-customers'),
            valueType: 'number',
            getValue: (s) => s.newCustomersCount,
            noSection: true
        },
        // TODO: masqué temporairement
        // { key: 'newUnpaidCount', categoryKey: 'flow-data', category: t('facturation.table-categories.flow-data'),
        //   label: t('facturation.table.new-unpaid'), valueType: 'number',
        //   getValue: (s) => Math.max(s.newUnpaidCount, 0), noSection: true },
        // { key: 'unpaidRegularizedCount', categoryKey: 'flow-data',
        //   category: t('facturation.table-categories.flow-data'),
        //   label: t('facturation.table.unpaid-regularized'), valueType: 'number',
        //   getValue: (s) => s.unpaidRegularizedCount, noSection: true },
        {
            key: 'newCancelationsCount',
            categoryKey: 'flow-data',
            category: t('facturation.table-categories.flow-data'),
            label: t('facturation.table.new-cancellations'),
            valueType: 'number',
            getValue: (s) => s.newCancelationsCount,
            noSection: true
        }
    ]

    if (billingAddress.retroCommissionDisplayRevenue) {
        rows.push({
            key: 'monthlyRevenueAmount',
            categoryKey: 'flow-data',
            category: t('facturation.table-categories.flow-data'),
            label: t('facturation.table.monthly-revenue'),
            valueType: 'currency',
            getValue: (s) => s.monthlyRevenueAmount,
            noSection: true
        })
    }

    rows.push({
        key: 'commissionAmount',
        categoryKey: 'commission',
        category: t('facturation.table-categories.commission'),
        label: t('facturation.table.commission-domiciliation'),
        valueType: 'currency',
        getValue: (s) => s.commissionAmount
    })

    if (showBookingRoom) {
        rows.push({
            key: 'bookingRoomCommissionAmount',
            categoryKey: 'commission',
            category: t('facturation.table-categories.commission'),
            label: t('facturation.table.commission-booking-room'),
            valueType: 'currency',
            getValue: (s) => s.bookingRoomCommissionAmount
        })
    }

    if (showBusinessReferral) {
        rows.push({
            key: 'businessReferralAmount',
            categoryKey: 'commission',
            category: t('facturation.table-categories.commission'),
            label: t('facturation.table.commission-business-referral'),
            valueType: 'currency',
            getValue: (s) => s.businessReferralAmount
        })
    }

    rows.push({
        key: 'commissionTotal',
        categoryKey: 'commission',
        category: t('facturation.table-categories.commission'),
        label: t('facturation.table.commission-total'),
        valueType: 'currency',
        getValue: (s) => s.commissionAmount + (s.bookingRoomCommissionAmount ?? 0) + (s.businessReferralAmount ?? 0)
    })

    return rows
}
